import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { SereneHeart, Wordmark } from "@/lib/brand";
import { ResetTestData } from "@/components/ResetTestData";

const STAT_KEYS = ["total", "AVAILABLE", "SENT", "RESERVED", "REDEEMED", "EXPIRED", "REVOKED"];
const CARD = "border border-[#DCD1BC] rounded-lg bg-[#FBF8F1]";
const FLD = "h-10 bg-white border-[#DCD1BC] text-[#2E2A22] placeholder:text-[#9C907A]";
const LBL = "text-[11px] tracking-[0.2em] text-[#6B6151] uppercase";
const BTN_DARK = "tracking-[0.2em] text-xs bg-[#2E2A22] text-[#F3EEE4] hover:bg-[#201C15]";
const BTN_OUTLINE = "tracking-[0.2em] text-xs border-[#C9BCA2] bg-transparent text-[#2E2A22] hover:bg-[#E8DFCF]";
const TABS = ["invitations", "members", "reviews", "orders", "audit", "settings"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("invitations");
  const [stats, setStats] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [audit, setAudit] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = useCallback(async () => {
    const [s, i, m, r, a, o] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/invitations"), api.get("/admin/members"),
      api.get("/admin/reviews"), api.get("/admin/audit"), api.get("/admin/orders"),
    ]);
    setStats(s.data); setInvitations(i.data.invitations); setMembers(m.data.members);
    setReviews(r.data.reviews); setAudit(a.data.logs); setOrders(o.data.orders);
  }, []);

  useEffect(() => { load(); }, [load]);
  const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changePassword = () => act(async () => {
    await api.post("/admin/change-password", pw);
    setPw({ current_password: "", new_password: "" });
  }, "Password updated");

  return (
    <div className="min-h-screen px-6 py-10 max-w-6xl mx-auto text-[#2E2A22]" style={{ backgroundColor: "#F3EEE4" }} data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3"><SereneHeart size={26} tone="ink" /><Wordmark height={18} testid="admin-wordmark" /><h1 className="font-serif text-2xl tracking-[0.25em] text-[#9C907A]">· ADMIN</h1></div>
        <button data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="text-xs tracking-[0.2em] text-[#6B6151] hover:text-[#2E2A22]">LOG OUT</button>
      </div>

      <div className="flex flex-wrap gap-6 mb-8 border-b border-[#DCD1BC]">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`pb-3 text-xs tracking-[0.3em] transition-colors ${tab === t ? "text-[#2E2A22] border-b-2 border-[#A98F63]" : "text-[#9C907A] hover:text-[#6B6151]"}`}>
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "invitations" && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-3 mb-8">
            {STAT_KEYS.map((k) => (
              <div key={k} data-testid={`stat-${k}`} className={`${CARD} p-4 text-center`}>
                <p className="text-2xl font-serif text-[#2E2A22]">{stats ? stats[k] ?? 0 : "—"}</p>
                <p className="text-[10px] tracking-[0.2em] text-[#9C907A] mt-1">{k.toUpperCase()}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mb-8">
            <Button data-testid="generate-1-btn" disabled={busy} onClick={() => act(() => api.post("/admin/invitations/generate", { count: 1 }), "Generated 1")} className={BTN_DARK}>GENERATE 1</Button>
            <Button data-testid="generate-10-btn" disabled={busy} onClick={() => act(() => api.post("/admin/invitations/generate", { count: 10 }), "Generated 10")} variant="outline" className={BTN_OUTLINE}>GENERATE 10</Button>
          </div>
          <div className={`${CARD} overflow-x-auto`}>
            <table className="w-full text-left text-xs" data-testid="invitations-table">
              <thead className="text-[#9C907A] tracking-[0.15em] border-b border-[#DCD1BC]"><tr>{["CODE", "STATUS", "SOURCE", "ACTIVATION", "CREATED", ""].map((h) => <th key={h} className="px-3 py-3 font-normal whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#EAE2D3]" data-testid={`inv-row-${inv.code}`}>
                    <td className="px-3 py-3 font-mono whitespace-nowrap">{inv.code}</td>
                    <td className="px-3 py-3">{inv.status}</td><td className="px-3 py-3">{inv.source_type}</td>
                    <td className="px-3 py-3">{inv.activation_status || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-[#6B6151]">{fmt(inv.created_at)}</td>
                    <td className="px-3 py-3">{["AVAILABLE", "SENT"].includes(inv.status) && <button data-testid={`revoke-${inv.code}`} onClick={() => act(() => api.post(`/admin/invitations/${inv.id}/revoke`), "Revoked")} className="text-[#A15B4B] hover:underline tracking-[0.15em]">REVOKE</button>}</td>
                  </tr>
                ))}
                {invitations.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#9C907A] tracking-[0.2em]">NO INVITATIONS YET</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "members" && (
        <div className={`${CARD} overflow-x-auto`} data-testid="members-admin">
          <table className="w-full text-left text-xs" data-testid="members-table">
            <thead className="text-[#9C907A] tracking-[0.15em] border-b border-[#DCD1BC]"><tr>{["NAME", "EMAIL", "SHOPIFY", "TAG", "INVITATION", "JOINED", ""].map((h) => <th key={h} className="px-3 py-3 font-normal whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-[#EAE2D3]" data-testid={`member-row-${m.id}`}>
                  <td className="px-3 py-3 whitespace-nowrap">{m.name || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{m.email}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-mono text-[#6B6151]">{m.shopify_customer_id || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{m.shopify_tagged ? "TAGGED" : "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{m.invitation_state || m.referral_status}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-[#6B6151]">{fmt(m.created_at)}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {m.member_status !== "REVOKED"
                      ? <button data-testid={`revoke-member-${m.id}`} onClick={() => { if (window.confirm(`Revoke membership for ${m.email}? Their Shopify tag will be removed.`)) act(() => api.post(`/admin/members/${m.id}/revoke`), "Member revoked"); }} className="text-[#A15B4B] hover:underline tracking-[0.15em]">REVOKE</button>
                      : <span className="text-[#9C907A] tracking-[0.15em]">REVOKED</span>}
                  </td>
                </tr>
              ))}
              {members.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#9C907A] tracking-[0.2em]">NO MEMBERS YET</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reviews" && (
        <div className={`${CARD} overflow-x-auto`} data-testid="reviews-admin">
          <table className="w-full text-left text-xs" data-testid="reviews-table">
            <thead className="text-[#9C907A] tracking-[0.15em] border-b border-[#DCD1BC]"><tr>{["MEMBER", "PRODUCT", "RATING", "REVIEW", "STATUS", "INVITATION", ""].map((h) => <th key={h} className="px-3 py-3 font-normal whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className="border-b border-[#EAE2D3] align-top" data-testid={`review-row-${r.id}`}>
                  <td className="px-3 py-3 whitespace-nowrap">{r.member_name || r.member_email}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.product_name}</td>
                  <td className="px-3 py-3 text-[#A98F63] whitespace-nowrap">{"★".repeat(r.rating || 0)}</td>
                  <td className="px-3 py-3 max-w-xs">{r.title && <p className="mb-1">{r.title}</p>}<p className="text-[#6B6151]">{r.review_text}</p></td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.status}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.invitation_activation_status}</td>
                  <td className="px-3 py-3 whitespace-nowrap space-x-3">{r.status === "PENDING" && (<>
                    <button data-testid={`approve-${r.id}`} onClick={() => act(() => api.post(`/admin/reviews/${r.id}/moderate`, { action: "approve" }), "Approved")} className="text-[#6E7B5B] hover:underline tracking-[0.15em]">APPROVE</button>
                    <button data-testid={`reject-${r.id}`} onClick={() => act(() => api.post(`/admin/reviews/${r.id}/moderate`, { action: "reject" }), "Rejected")} className="text-[#A15B4B] hover:underline tracking-[0.15em]">REJECT</button></>)}</td>
                </tr>
              ))}
              {reviews.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#9C907A] tracking-[0.2em]">NO REVIEWS YET</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className={`${CARD} overflow-x-auto`} data-testid="orders-admin">
          <table className="w-full text-left text-xs" data-testid="orders-table">
            <thead className="text-[#9C907A] tracking-[0.15em] border-b border-[#DCD1BC]"><tr>{["ORDER", "MEMBER", "STATUS", "ITEMS", "TOTAL", "DATE"].map((h) => <th key={h} className="px-3 py-3 font-normal whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#EAE2D3]" data-testid={`order-row-${o.id}`}>
                  <td className="px-3 py-3 font-mono whitespace-nowrap">{o.order_name || o.shopify_order_id}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {o.non_member
                      ? <span data-testid={`order-nonmember-${o.id}`} className="text-[10px] tracking-[0.15em] px-2 py-0.5 border border-[#C9BCA2] text-[#A15B4B]">NON_MEMBER</span>
                      : <span>{o.member_name || o.member_email || "—"}</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">{o.status}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-[#6B6151]">{o.item_count}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{o.total != null ? `${(o.total).toFixed(2)} ${o.currency || ""}`.trim() : "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-[#6B6151]">{fmt(o.created_at)}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#9C907A] tracking-[0.2em]">NO ORDERS YET</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "audit" && (
        <div className={`${CARD} overflow-x-auto`} data-testid="audit-admin">
          <table className="w-full text-left text-xs" data-testid="audit-table">
            <thead className="text-[#9C907A] tracking-[0.15em] border-b border-[#DCD1BC]"><tr>{["EVENT", "ACTOR", "MEMBER", "WHEN"].map((h) => <th key={h} className="px-3 py-3 font-normal whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {audit.map((l) => (
                <tr key={l.id} className="border-b border-[#EAE2D3]" data-testid={`audit-row-${l.id}`}>
                  <td className="px-3 py-3 whitespace-nowrap">{l.event}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-mono text-[#6B6151]">{l.actor_id || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-mono text-[#6B6151]">{l.member_id || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-[#6B6151]">{fmt(l.timestamp)}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-[#9C907A] tracking-[0.2em]">NO AUDIT EVENTS</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && (
        <div className={`${CARD} p-6 max-w-md space-y-4`} data-testid="settings-admin">
          <p className="text-[11px] tracking-[0.25em] text-[#A98F63]">CHANGE PASSWORD</p>
          <div><label className={LBL}>Current password</label><Input data-testid="current-password-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={FLD} /></div>
          <div><label className={LBL}>New password (min 12)</label><Input data-testid="new-password-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={FLD} /></div>
          <Button data-testid="change-password-btn" disabled={busy || !pw.current_password || pw.new_password.length < 12} onClick={changePassword} className={BTN_DARK}>UPDATE PASSWORD</Button>
        </div>
      )}
      {tab === "settings" && <div className="mt-6"><ResetTestData onDone={load} /></div>}
    </div>
  );
}
